/**
 * The bit of markdown the /answer model writes, the way Cikisi's Ask Mila
 * renders it (its textToHtml pipe): **bold**, *italic*, `code`, bullet and
 * numbered lists, # headings, paragraphs. Parsed into parts the template
 * renders, not turned into an HTML string, so third-party model text never
 * goes through innerHTML.
 */

export interface Inline {
  text: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
}

export type Block =
  | { kind: 'p' | 'h'; inlines: Inline[] }
  | { kind: 'ul' | 'ol'; items: Inline[][] };

const BULLET = /^[-*•+]\s+(.*)$/;
const NUMBERED = /^\d+[.)]\s+(.*)$/;
const HEADING = /^#{1,6}\s+(.*?)\s*#*$/;

export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  // In one object (not two lets) so the flushes below can reset it and
  // TypeScript still sees what it holds.
  const open: { para: string[]; list: { kind: 'ul' | 'ol'; items: string[] } | null } = { para: [], list: null };

  const flushPara = () => {
    if (open.para.length) blocks.push({ kind: 'p', inlines: parseInline(open.para.join('\n')) });
    open.para = [];
  };
  const flushList = () => {
    if (open.list) blocks.push({ kind: open.list.kind, items: open.list.items.map(parseInline) });
    open.list = null;
  };

  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    const item = bullet ?? numbered;
    if (item) {
      flushPara();
      const kind = bullet ? 'ul' : 'ol';
      if (open.list?.kind !== kind) {
        flushList();
        open.list = { kind, items: [] };
      }
      open.list.items.push(item[1]);
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flushPara();
      flushList();
      blocks.push({ kind: 'h', inlines: parseInline(heading[1]) });
      continue;
    }
    if (open.list) {
      // A wrapped line of the last list item.
      open.list.items[open.list.items.length - 1] += ' ' + line;
      continue;
    }
    open.para.push(line);
  }
  flushPara();
  flushList();
  return blocks;
}

/** **bold** and __bold__, *italic*, `code`. Single underscores are left
 *  alone: they show up in URLs and identifiers far more than as italics. */
const INLINE = /(\*\*[^*]+?\*\*|__[^_]+?__|`[^`]+?`|\*[^*\s](?:[^*]*?[^*\s])?\*)/g;

export function parseInline(text: string): Inline[] {
  return text.split(INLINE).filter(Boolean).map(part => {
    if ((part.startsWith('**') && part.endsWith('**')) || (part.startsWith('__') && part.endsWith('__'))) {
      return { text: part.slice(2, -2), bold: true };
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) return { text: part.slice(1, -1), code: true };
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return { text: part.slice(1, -1), italic: true };
    return { text: part };
  });
}
